/* eslint-disable react/prop-types */
import { Currency, CurrencyAmount, Percent, Price, Token } from '@kodiak-finance/sdk-core'
import { FeeAmount, Position } from '@kodiak-finance/v3-sdk'
import Badge from 'components/Badge'
import RangeBadge from 'components/Badge/RangeBadge'
import { CollectFeesButton } from 'components/CollectFeesButton'
import CurrencyLogo from 'components/CurrencyLogo'
import DoubleCurrencyLogo from 'components/DoubleLogo'
import Loader from 'components/Loader'
import { CurrentPriceDisplay, PriceRangeDisplay } from 'components/PriceDisplay'
import Row from 'components/Row'
import { MouseoverTooltip } from 'components/Tooltip'
import { CHAIN_IDS_TO_NAMES, SupportedChainId } from 'config/chains'
import { useTokens } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import { useCurrencyId } from 'hooks/useCurrencyId'
import { usePool } from 'hooks/usePools'
import { useProcessedPoolData } from 'hooks/useProcessedPoolData'
import { useV3PositionFees } from 'hooks/useV3PositionFees'
import { useTokenPricing } from 'hooks/useV3TokenPricing'
import { useContext, useMemo } from 'react'
import React from 'react'
import { Bound } from 'state/mint/v3/actions'
import styled, { ThemeContext } from 'styled-components'
import { StyledInternalLink, ThemedText } from 'theme'
import { PositionDetails } from 'types/position'
import { formatPrice } from 'utils/formatCurrencyAmount'
import { formatDollarAmount } from 'utils/formatDollarAmt'
import { formatTickPriceForRangeDisplay, getOptimalSigFigs } from 'utils/formatTickPrice'
import { getQuoteCurrency } from 'utils/getQuoteToken'
import { unwrappedToken } from 'utils/unwrappedToken'

import { WRAPPED_NATIVE_CURRENCY } from '../../constants/tokens'
import * as S from '../PositionList/styled'
import { ResponsiveButtonPrimary } from '../PositionList/styled'
import * as ItemS from './styled'

const StyledContainer = styled.div`
  display: table-row;
  transition: background-color 0.2s;
  border-bottom: 1px solid;
  border-color: ${({ theme }) => theme.cardBorderSecondary};
  text-decoration: none;
  color: inherit;

  &:last-child {
    border-bottom: none;
  }

  :hover {
    background-color: ${({ theme }) => theme.cardSecondary};
    border-color: ${({ theme }) => theme.cardBorderPrimary};
  }

  td {
    display: table-cell;
  }
`

const RangeLineItem = styled(ItemS.DataLineItem)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  width: 100%;

  ${({ theme }) => theme.mediaWidth.upToSmall`
  // background-color: ${({ theme }) => theme.textSecondary};
  //   border-radius: 12px;
  //   padding: 8px 0;
`};
`

const RangeText = styled.div`
  /* background-color: ${({ theme }) => theme.deprecated_bg2}; */
  padding: 0.25rem 0;
  font-size: 12px;

  border-radius: 8px;

  color: ${({ theme }) => theme.textPrimary};
  display: flex;
  flex-direction: row;
  align-items: center;
  & > *:not(:last-child) {
    margin-right: 4px;
  }
`

export const ExtentsText = styled.span`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 12px;
  margin-right: 8px;
  ${({ theme }) => theme.mediaWidth.upToSmall`
    display: none;
  `};
`

const CompactRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 1px 0;
`

const RetryLink = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.accentAction};
  font-size: 10px;
  cursor: pointer;
  padding: 0;
  margin-top: 4px;
  text-decoration: underline;

  &:hover {
    color: ${({ theme }) => theme.accentActionSoft};
  }

  &:disabled {
    color: ${({ theme }) => theme.textTertiary};
    cursor: not-allowed;
  }
`

const PriceFailureText = styled(ThemedText.DeprecatedLargeHeader)`
  position: relative;
  cursor: help;

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    height: 1px;
    background: currentColor;
    opacity: 0.3;
  }
`

interface PriceFailureDisplayProps {
  amount0ForDisplay?: CurrencyAmount<Token>
  amount1ForDisplay?: CurrencyAmount<Token>
  token0?: Token
  token1?: Token
  onRetry?: () => void
  isRetrying?: boolean
}

interface PositionListItemProps {
  positionDetails: PositionDetails
  row: number
}

export function getPriceOrderingFromPositionForUI(position?: Position): {
  priceLower?: Price<Token, Token>
  priceUpper?: Price<Token, Token>
  quote?: Token
  base?: Token
} {
  if (!position) {
    return {}
  }

  const token0 = position.amount0.currency
  const token1 = position.amount1.currency

  // if token0 is a dollar-stable asset, set it as the quote token
  // const stables = [DAI, USDC_MAINNET, USDT]
  const stables: any[] = []
  if (stables.some((stable) => stable.equals(token0))) {
    return {
      priceLower: position.token0PriceUpper.invert(),
      priceUpper: position.token0PriceLower.invert(),
      quote: token0,
      base: token1,
    }
  }

  // if token1 is an ETH-/BTC-stable asset, set it as the base token
  const bases = [...Object.values(WRAPPED_NATIVE_CURRENCY)]
  if (bases.some((base) => base && base.equals(token1))) {
    return {
      priceLower: position.token0PriceUpper.invert(),
      priceUpper: position.token0PriceLower.invert(),
      quote: token0,
      base: token1,
    }
  }

  // if both prices are below 1, invert
  if (position.token0PriceUpper.lessThan(1)) {
    return {
      priceLower: position.token0PriceUpper.invert(),
      priceUpper: position.token0PriceLower.invert(),
      quote: token0,
      base: token1,
    }
  }

  // otherwise, just return the default
  return {
    priceLower: position.token0PriceLower,
    priceUpper: position.token0PriceUpper,
    quote: token1,
    base: token0,
  }
}

export function getCurrencyOrdering(currencyA: Currency | undefined, currencyB: Currency | undefined) {
  if (!currencyA || !currencyB) return { currencyQuote: undefined, currencyBase: undefined }

  const currencyQuote = getQuoteCurrency(currencyA, currencyB)
  const currencyBase = currencyQuote === currencyA ? currencyB : currencyA

  return { currencyQuote, currencyBase }
}

function PositionListItem({ positionDetails, row }: PositionListItemProps) {
  const { chainId, account } = useConnection()
  const chainName = CHAIN_IDS_TO_NAMES[chainId as SupportedChainId]
  const theme = useContext(ThemeContext)

  const {
    token0: token0Address,
    token1: token1Address,
    fee: feeAmount,
    liquidity: liquidityString,
    tickLower,
    tickUpper,
    inRange,
    poolTick,
    tokenId,
    pool,
    owner,
  } = positionDetails
  const tokenAddresses = useMemo(() => [token0Address, token1Address], [token0Address, token1Address])
  const [token0, token1] = useTokens(tokenAddresses)
  const currencyA = token0 ? unwrappedToken(token0) : undefined
  const currencyB = token1 ? unwrappedToken(token1) : undefined

  const currencyAId = useCurrencyId(currencyA)
  const currencyBId = useCurrencyId(currencyB)

  // call here, so it's available in increase liquidity page since it is cached, simple fix for now. Rewrite later
  usePool(currencyA, currencyB, feeAmount)

  const { liquidity } = useMemo(
    () => ({
      liquidity: BigInt(liquidityString),
    }),
    [liquidityString]
  )

  const removed = liquidity === 0n

  const feeHookParams = useMemo(
    () => ({
      position: removed ? undefined : positionDetails,
      token0,
      token1,
      asWETH: false,
    }),
    [removed, positionDetails, token0, token1]
  )

  const [feeValue0, feeValue1] = useV3PositionFees(feeHookParams)

  const tokenPricingParams = useMemo(
    () => ({
      token0,
      token1,
      feeValue0: feeValue0 as CurrencyAmount<Token> | undefined,
      feeValue1: feeValue1 as CurrencyAmount<Token> | undefined,
      liquidity: liquidityString,
      tickLower: tickLower ?? 0,
      tickUpper: tickUpper ?? 0,
      currentTick: positionDetails.poolTick ?? 0,
      feeAmount,
      poolLiquidity: pool.liquidity,
      sqrtPriceX96: pool.sqrtPrice,
      isClosedPosition: removed,
    }),
    [
      token0,
      token1,
      feeValue0,
      feeValue1,
      liquidityString,
      tickLower,
      tickUpper,
      positionDetails.poolTick,
      feeAmount,
      pool.liquidity,
      pool.sqrtPrice,
      removed,
    ]
  )

  const {
    fiatValueOfFees,
    fiatValueOfLiquidity,
    amount0ForDisplay,
    amount1ForDisplay,
    pricesLoading,
    tokenPriceMap,
    pricesError,
    retryPrices,
  } = useTokenPricing(tokenPricingParams)

  const { actualToken0Percentage, actualToken1Percentage } = useMemo(() => {
    if (removed || !amount0ForDisplay || !amount1ForDisplay) {
      return { actualToken0Percentage: 0, actualToken1Percentage: 0 }
    }

    const hasToken0 = amount0ForDisplay.greaterThan(0)
    const hasToken1 = amount1ForDisplay.greaterThan(0)

    if (hasToken0 && !hasToken1) {
      return { actualToken0Percentage: 100, actualToken1Percentage: 0 }
    } else if (!hasToken0 && hasToken1) {
      return { actualToken0Percentage: 0, actualToken1Percentage: 100 }
    }

    if (hasToken0 && hasToken1 && token0 && token1) {
      const token0Price = tokenPriceMap?.[token0.address]
      const token1Price = tokenPriceMap?.[token1.address]

      if (token0Price && token1Price && !pricesLoading) {
        try {
          const amount0USD = parseFloat(amount0ForDisplay.toExact()) * token0Price
          const amount1USD = parseFloat(amount1ForDisplay.toExact()) * token1Price
          const totalUSD = amount0USD + amount1USD

          if (totalUSD > 0) {
            const token0Pct = Math.round((amount0USD / totalUSD) * 100)
            return { actualToken0Percentage: token0Pct, actualToken1Percentage: 100 - token0Pct }
          }
        } catch (error) {
          console.error('Error calculating USD percentages:', error)
        }
      }
    }

    return { actualToken0Percentage: 0, actualToken1Percentage: 0 }
  }, [amount0ForDisplay, amount1ForDisplay, tokenPriceMap, token0, token1, pricesLoading, removed])

  const fallbackData = useMemo(() => {
    if (typeof tickLower !== 'number' || typeof tickUpper !== 'number' || typeof poolTick !== 'number') {
      return undefined
    }

    return {
      lowerTick: tickLower,
      upperTick: tickUpper,
      currentTick: poolTick,
      feeTier: feeAmount,
      poolType: 'V3' as const,
    }
  }, [tickLower, tickUpper, poolTick, feeAmount])

  const processedData = useProcessedPoolData(undefined, token0, token1, fallbackData)

  const outOfRange = !inRange
  const positionSummaryLink = `/liquidity/v3/${positionDetails.tokenId}?chain=${chainName}`

  const formattedPrices = useMemo(() => {
    if (!processedData) return { lower: null, upper: null, quote: null }
    const lowerDisplay = formatTickPriceForRangeDisplay(
      processedData.priceLower,
      processedData.isLowerPriceAtLimit,
      Bound.LOWER,
      feeAmount
    )
    const upperDisplay = formatTickPriceForRangeDisplay(
      processedData.priceUpper,
      processedData.isUpperPriceAtLimit,
      Bound.UPPER,
      feeAmount
    )

    return {
      lower: lowerDisplay,
      upper: upperDisplay,
      quote: processedData.quotePrice
        ? feeAmount === FeeAmount.LOWEST
          ? processedData.quotePrice.toSignificant(5)
          : formatPrice(processedData.quotePrice, getOptimalSigFigs(processedData.quotePrice))
        : null,
    }
  }, [feeAmount, processedData])

  const displayOrientationRequiresInversion = useMemo(() => {
    return processedData?.currencyQuote?.wrapped.equals(token0?.wrapped)
  }, [processedData?.currencyQuote, token0])

  const { currencyQuote, currencyBase } = getCurrencyOrdering(currencyA, currencyB)

  const inverted = displayOrientationRequiresInversion

  const liquidityValueUpper = inverted ? amount0ForDisplay : amount1ForDisplay
  const liquidityValueLower = inverted ? amount1ForDisplay : amount0ForDisplay

  if (!processedData) {
    return (
      <ItemS.LinkRow to={positionSummaryLink} row={row + 1}>
        <ItemS.PrimaryPositionIdData>
          <S.FlexRowCenter>
            <DoubleCurrencyLogo currency0={currencyA} currency1={currencyB} size={32} margin />
            <S.FlexCol style={{ gap: '0px' }}>
              <S.FlexRowCenter>
                <ItemS.PairText>
                  {currencyA?.symbol}-{currencyB?.symbol}
                </ItemS.PairText>
                <ItemS.StategyBadge>{new Percent(feeAmount, 1_000_000).toSignificant()}%</ItemS.StategyBadge>
              </S.FlexRowCenter>
              <RangeBadge removed={removed} inRange={!outOfRange} />
            </S.FlexCol>
          </S.FlexRowCenter>
        </ItemS.PrimaryPositionIdData>
        <S.Td>
          <Loader />
        </S.Td>
        <S.Td></S.Td>
        <S.Td></S.Td>
        <S.Td></S.Td>
      </ItemS.LinkRow>
    )
  }

  return (
    <StyledContainer>
      <S.Td>
        <ItemS.PrimaryPositionIdData>
          <S.FlexRowCenter>
            <DoubleCurrencyLogo currency0={currencyA} currency1={currencyB} size={32} margin />
            <S.FlexCol style={{ gap: '0px' }}>
              <S.FlexRowCenter>
                <ItemS.PairText>
                  {currencyA?.symbol}-{currencyB?.symbol}
                </ItemS.PairText>
                <ItemS.StategyBadge>{new Percent(feeAmount, 1_000_000).toSignificant()}%</ItemS.StategyBadge>
              </S.FlexRowCenter>
              <RangeBadge removed={removed} inRange={!outOfRange} />
            </S.FlexCol>
          </S.FlexRowCenter>
        </ItemS.PrimaryPositionIdData>
      </S.Td>

      <S.Td>
        <PriceRangeDisplay
          currencyBase={processedData?.currencyBase}
          currencyQuote={processedData?.currencyQuote}
          priceLower={processedData?.priceLower}
          priceUpper={processedData?.priceUpper}
          formattedPriceLower={formattedPrices.lower}
          formattedPriceUpper={formattedPrices.upper}
        />
      </S.Td>

      <S.Td>
        <RangeLineItem>
          <CurrentPriceDisplay
            currencyBase={processedData?.currencyBase}
            currencyQuote={processedData?.currencyQuote}
            quotePrice={processedData?.quotePrice}
            formattedQuotePrice={formattedPrices.quote}
          />
        </RangeLineItem>
      </S.Td>

      <S.Td>
        {fiatValueOfLiquidity === undefined || fiatValueOfLiquidity === null ? (
          <ThemedText.DeprecatedLargeHeader color={theme.textPrimary} fontSize="24px" fontWeight={500}>
            <Loader />
          </ThemedText.DeprecatedLargeHeader>
        ) : fiatValueOfLiquidity ? (
          <MouseoverTooltip
            text={
              <S.FlexCol style={{ gap: '8px', minWidth: '120px' }}>
                <S.FlexRowCenter style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <S.FlexRowCenter style={{ gap: '0.5rem', alignItems: 'center' }}>
                    <CurrencyLogo currency={currencyQuote} size="20px" />
                    <ThemedText.DeprecatedMain fontSize={12}>
                      {liquidityValueUpper ? liquidityValueUpper.toSignificant(4) : '-'}
                    </ThemedText.DeprecatedMain>
                  </S.FlexRowCenter>
                  {typeof actualToken0Percentage === 'number' && !removed ? (
                    <Badge>
                      <ThemedText.DeprecatedMain fontSize={11}>
                        {currencyQuote?.wrapped.equals(token0?.wrapped)
                          ? actualToken0Percentage
                          : actualToken1Percentage}
                        %
                      </ThemedText.DeprecatedMain>
                    </Badge>
                  ) : null}
                </S.FlexRowCenter>
                <S.FlexRowCenter style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <S.FlexRowCenter style={{ gap: '0.5rem', alignItems: 'center' }}>
                    <CurrencyLogo currency={currencyBase} size="20px" />
                    <ThemedText.DeprecatedMain fontSize={12}>
                      {liquidityValueLower ? liquidityValueLower.toSignificant(4) : '-'}
                    </ThemedText.DeprecatedMain>
                  </S.FlexRowCenter>
                  {typeof actualToken1Percentage === 'number' && !removed ? (
                    <Badge>
                      <ThemedText.DeprecatedMain fontSize={11}>
                        {currencyBase?.wrapped?.equals(token0?.wrapped)
                          ? actualToken0Percentage
                          : actualToken1Percentage}
                        %
                      </ThemedText.DeprecatedMain>
                    </Badge>
                  ) : null}
                </S.FlexRowCenter>
              </S.FlexCol>
            }
          >
            <ThemedText.DeprecatedLargeHeader fontSize="14px" fontWeight={400} color={theme.textPrimary}>
              <>{formatDollarAmount(fiatValueOfLiquidity)}</>
            </ThemedText.DeprecatedLargeHeader>
          </MouseoverTooltip>
        ) : (
          <ThemedText.DeprecatedLargeHeader color={theme.textPrimary} fontSize="16px" fontWeight={400}>
            $0
          </ThemedText.DeprecatedLargeHeader>
        )}
      </S.Td>

      <S.Td>
        <Row gap="0.5rem" alignItems="flex-start">
          <div style={{ minWidth: '50px' }}>
            {fiatValueOfFees === undefined || fiatValueOfFees === null ? (
              <ThemedText.DeprecatedLargeHeader color={theme.deprecated_text1} fontSize="24px" fontWeight={500}>
                <Loader />
              </ThemedText.DeprecatedLargeHeader>
            ) : fiatValueOfFees ? (
              <ThemedText.DeprecatedLargeHeader color={theme.accentAction} fontSize="14px" fontWeight={400}>
                <>{formatDollarAmount(fiatValueOfFees)}</>
              </ThemedText.DeprecatedLargeHeader>
            ) : (
              <ThemedText.DeprecatedLargeHeader color={theme.textSecondary} fontSize="14px" fontWeight={400}>
                $0
              </ThemedText.DeprecatedLargeHeader>
            )}
          </div>
          <CollectFeesButton
            tokenId={tokenId}
            token0={token0 ? unwrappedToken(token0).wrapped : undefined}
            token1={token1 ? unwrappedToken(token1).wrapped : undefined}
            feeValue0={feeValue0 as CurrencyAmount<Token> | undefined}
            feeValue1={feeValue1 as CurrencyAmount<Token> | undefined}
            fiatValueOfFees={fiatValueOfFees}
            owner={owner}
            currentAccount={account}
            showTooltip={true}
          />
        </Row>
      </S.Td>

      <S.Td>
        {!removed ? (
          <ItemS.ManageButtonFlex>
            <ResponsiveButtonPrimary
              data-cy="add-v2-button"
              id="add-v2-button"
              as={StyledInternalLink}
              to={`/liquidity/v3/increase/${currencyAId}/${currencyBId}/${feeAmount}/${positionDetails.tokenId}`}
            >
              +
            </ResponsiveButtonPrimary>
            <ResponsiveButtonPrimary
              data-cy="remove-v2-button"
              id="remove-v2-button"
              as={StyledInternalLink}
              to={`/liquidity/v3/remove/${positionDetails.tokenId}`}
            >
              -
            </ResponsiveButtonPrimary>
          </ItemS.ManageButtonFlex>
        ) : (
          ''
        )}
      </S.Td>
    </StyledContainer>
  )
}

export default React.memo(PositionListItem, (prevProps, nextProps) => {
  if (prevProps === nextProps) return true

  if (prevProps.row !== nextProps.row) return false

  const prevPos = prevProps.positionDetails
  const nextPos = nextProps.positionDetails

  if (prevPos === nextPos) return true

  const criticalPropsEqual =
    prevPos.tokenId === nextPos.tokenId &&
    prevPos.liquidity === nextPos.liquidity &&
    prevPos.tickLower === nextPos.tickLower &&
    prevPos.tickUpper === nextPos.tickUpper &&
    prevPos.inRange === nextPos.inRange &&
    prevPos.token0 === nextPos.token0 &&
    prevPos.token1 === nextPos.token1 &&
    prevPos.fee === nextPos.fee &&
    prevPos.poolTick === nextPos.poolTick

  if (!criticalPropsEqual) return false

  const poolPropsEqual =
    prevPos.pool?.liquidity === nextPos.pool?.liquidity &&
    prevPos.pool?.sqrtPrice === nextPos.pool?.sqrtPrice &&
    prevPos.pool?.id === nextPos.pool?.id

  return poolPropsEqual
})
